import {
  DownOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Breadcrumb, Col, DatePicker, Dropdown, Empty, Form, Row, Spin } from "antd";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import useDayGlance, { NUMERIC_FIELD_MAP, NUMERIC_KEYS, SOURCE_CONFIG } from "./Usedayglance";
import { handlePrint } from "./DayGlancePrint";
import { handleDayGlancePdf } from "./DayGlancePdf";
import { handleDayGlanceExcel } from "./DayGlanceExcel";

const { RangePicker } = DatePicker;

// HEADERS order: REF NO / DATE / CUST NAME are shown as their own
// columns now (single row per transaction — no more detail sub-row),
// followed by the NUMERIC_KEYS sequence so the two can still be walked
// together for the numeric cells.
const HEADERS = [
  "REF NO",
  "DATE",
  "CUST NAME",
  "QTY",
  "TOT AMT",
  "DISCOUNT",
  "NET AMT",
  "CASH",
  "CARD",
  "UPI",
  "ONLINE",
  "BALANCE",
  "CHEQUE",
];
// How many leading HEADERS entries are non-numeric labels (REF NO, DATE,
// CUST NAME) rather than a NUMERIC_KEYS column — used to line up the
// Grand Totals header row, which only shows the numeric columns.
const LABEL_COLUMN_COUNT = 3;

const TABS = [{ key: "ALL", label: "All" }, ...SOURCE_CONFIG];

const TAB_COLORS = {
  ALL: "#0E7C86",
  SALE: "#10B981",
  SALE_RETURN: "#EF4444",
  PURCHASE: "#F59E0B",
  PURCHASE_RETURN: "#8B5CF6",
  SUPPLIER_PAYMENT: "#3B82F6",
  RECEIPT: "#06B6D4",
  BANK_ENTRIES: "#F97316",
  JOURNAL_RECEIPT: "#EC4899",
  JOURNAL_PAYMENT: "#6366F1",
};

// Per-payment-mode accent — mirrors the vivid per-method palette already
// used in PaymentModesDialog elsewhere in the app (each mode gets its
// own bright, recognizable color rather than one flat table color).
const PAYMENT_COLORS = {
  CASH: "#10B981",
  CARD: "#3B82F6",
  UPI: "#8B5CF6",
  ONLINE: "#06B6D4",
  CHEQUE: "#F59E0B",
  BALANCE: "#EF4444",
};

const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Same tint math as hexToRgba but flattened to an OPAQUE rgb() by
// blending against white, instead of leaving alpha transparency. The
// header row is `position: sticky` so it needs a solid backing color —
// an rgba() background would let scrolled-past rows show through it.
const tintOpaque = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const blend = (c) => Math.round(c * alpha + 255 * (1 - alpha));
  return `rgb(${blend(r)}, ${blend(g)}, ${blend(b)})`;
};

const THEME = {
  headerBg: "linear-gradient(135deg, #F0FBFC 0%, #E1F5F7 45%, #D5F3F0 100%)",
  border: "#CDEEF1",
  accent: "#0E7C86",
};

const fmt2 = (v) => (parseFloat(v) || 0).toFixed(2);

const headerCellStyle = (color) => ({
  backgroundColor: tintOpaque(color, 0.12),
  color,
  fontWeight: 700,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  padding: "10px 8px",
  textAlign: "center",
  borderBottom: `2px solid ${hexToRgba(color, 0.35)}`,
  position: "sticky",
  top: 0,
  zIndex: 1,
});

const MotionTag = ({ active, onClick, color = "#0E7C86", children }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    transition={{ type: "spring", stiffness: 400, damping: 20 }}
    onClick={onClick}
    style={{
      cursor: "pointer",
      padding: "6px 12px",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 20,
      border: `1px solid ${active ? color : hexToRgba(color, 0.35)}`,
      color: active ? "#fff" : "#0E7C86",
      background: active
        ? `linear-gradient(135deg, ${color} 0%, ${hexToRgba(color, 0.85)} 100%)`
        : "#ffffff",
      boxShadow: active
        ? `0 4px 10px ${hexToRgba(color, 0.3)}`
        : `0 1px 3px ${hexToRgba(color, 0.1)}`,
      transition: "border-color 0.2s, background 0.2s",
      userSelect: "none",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </motion.div>
);

// One rounded, bordered card per source — grouped tinted sticky header,
// circular S.No badges, everything for a transaction on a single row
// (Ref No / Date / Cust Name / amounts), a colored totals row, and the
// whole body capped at 400px with its own scrollbar so a busy tab
// doesn't stretch the page.
const DayGlanceSection = ({ label, color, rows, totals }) => (
  <div className="dg-card" style={{ borderTop: `4px solid ${color}` }}>
    <div className="dg-card-header">
      <h3 className="dg-title" style={{ color }}>
        {label}
      </h3>
      <span className="dg-title-underline" style={{ background: color }} />
    </div>

    <div className="table-container">
      <table className="dg-table">
        <thead>
          <tr>
            <th style={headerCellStyle(color)}>S.No</th>
            {HEADERS.map((h) => (
              <th key={h} style={headerCellStyle(color)}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <tr className="dg-data-row" key={`${label}-${item.billno}-${idx}`}>
              <td className="dg-sno-cell">
                <span className="dg-sno-badge" style={{ borderColor: color, color }}>
                  {idx + 1}
                </span>
              </td>
              <td className="dg-cell dg-cell-refno">{item.billno}</td>
              <td className="dg-cell dg-cell-date">
                {item.BillDate ? dayjs(item.BillDate).format("DD/MM/YYYY") : ""}
              </td>
              <td className="dg-cell dg-cell-cust">{item.CustName}</td>
              {NUMERIC_KEYS.map((key) => (
                <td
                  key={key}
                  className="dg-cell"
                  style={{
                    textAlign: "center",
                    color: PAYMENT_COLORS[key] || "#1F2A37",
                    fontWeight: PAYMENT_COLORS[key] ? 700 : 600,
                  }}
                >
                  {item[NUMERIC_FIELD_MAP[key]]}
                </td>
              ))}
            </tr>
          ))}

          <tr className="dg-total-row" style={{ background: tintOpaque(color, 0.16) }}>
            <td className="dg-total-cell" />
            <td className="dg-total-cell" colSpan={LABEL_COLUMN_COUNT} style={{ color }}>
              Total
            </td>
            {NUMERIC_KEYS.map((key) => (
              <td
                key={key}
                className="dg-total-cell"
                style={{ textAlign: "center", color: PAYMENT_COLORS[key] || color }}
              >
                {key === "QTY" ? totals[key] : fmt2(totals[key])}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

const DayGlance = () => {
  const [dates, setDates] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [activeTab, setActiveTab] = useState("ALL");

  const { loading, tabsData, allRows, allTotals } = useDayGlance(dates);

  const isAllTab = activeTab === "ALL";
  const activeTabLabel =
    TABS.find((t) => t.key === activeTab)?.label || "Day Glance";

  const sections = useMemo(() => {
    if (isAllTab) {
      return SOURCE_CONFIG.map((cfg) => ({
        key: cfg.key,
        label: cfg.label,
        rows: tabsData[cfg.key]?.rows || [],
        totals: tabsData[cfg.key]?.totals,
      })).filter((s) => s.rows.length > 0);
    }
    return [
      {
        key: activeTab,
        label: activeTabLabel,
        rows: tabsData[activeTab]?.rows || [],
        totals: tabsData[activeTab]?.totals,
      },
    ];
  }, [isAllTab, activeTab, activeTabLabel, tabsData]);

  const totalRowCount = isAllTab
    ? allRows.length
    : sections[0]?.rows.length || 0;

  const handleRangeChange = (values) => {
    if (!values || !values[0] || !values[1]) return;
    setDates([values[0], values[1]]);
  };

  const isExportDisabled = !loading && totalRowCount === 0;

  // Rebuilt from tabsData in SOURCE_CONFIG order so it stays in sync
  // automatically as sources get wired up — same flat argument shape
  // (filteredData, saleReturnData, purchaseData, ...) the existing
  // DayGlancePrint / DayGlancePdf / DayGlanceExcel handlers expect.
  const exportArgs = SOURCE_CONFIG.map((cfg) => tabsData[cfg.key]?.rows || []);

  const exportMenuItems = [
    {
      key: "print",
      icon: (
        <PrinterOutlined
          style={{ color: isExportDisabled ? undefined : "#0E7C86" }}
        />
      ),
      label: (
        <span
          style={{ fontWeight: 600, color: isExportDisabled ? undefined : "#0E7C86" }}
        >
          Print
        </span>
      ),
      onClick: () => handlePrint(...exportArgs),
      disabled: isExportDisabled,
    },
    {
      key: "pdf",
      icon: (
        <FilePdfOutlined
          style={{ color: isExportDisabled ? undefined : "#cf1322" }}
        />
      ),
      label: (
        <span
          style={{ fontWeight: 600, color: isExportDisabled ? undefined : "#cf1322" }}
        >
          Export PDF
        </span>
      ),
      onClick: () => handleDayGlancePdf(...exportArgs),
      disabled: isExportDisabled,
    },
    {
      key: "excel",
      icon: (
        <FileExcelOutlined
          style={{ color: isExportDisabled ? undefined : "#1a7f37" }}
        />
      ),
      label: (
        <span
          style={{ fontWeight: 600, color: isExportDisabled ? undefined : "#1a7f37" }}
        >
          Export Excel
        </span>
      ),
      onClick: () => handleDayGlanceExcel(...exportArgs),
      disabled: isExportDisabled,
    },
  ];

  return (
    <div
      id="printableArea"
      style={{
        // backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        // maxHeight: "100vh",
        // overflowY: "auto",
        // height: "fit-content",
      }}
    >
      {/* <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
        <Col>
          <Breadcrumb style={{ fontSize: 18, fontWeight: 600, color: "#0C1154" }}>
            <Breadcrumb.Item>Point Of Sale</Breadcrumb.Item>
            <Breadcrumb.Item>Day Glance</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row> */}

      <div
        className="day-glance-card"
        style={{
          position: "relative",
          background: THEME.headerBg,
          border: `1px solid ${THEME.border}`,
          borderRadius: 16,
          boxShadow: `0 6px 20px ${hexToRgba(THEME.accent, 0.18)}`,
          padding: 20,
          color: "#1F2A37",
          marginBottom: 16,
        }}
      >
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <span className="dg-page-title" style={{ color: THEME.accent }}>
              Day Glance
            </span>
            <span
              className="dg-title-underline"
              style={{ background: THEME.accent, display: "block", marginTop: 6 }}
            />
          </Col>
        </Row>

        <Row
          justify="space-between"
          align="middle"
          gutter={[16, 16]}
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Form.Item
              label={
                <span style={{ color: THEME.accent, fontWeight: 600 }}>
                  Date Range
                </span>
              }
              style={{ marginBottom: 0 }}
            >
              <RangePicker
                value={dates}
                format="DD MMM YYYY"
                onChange={handleRangeChange}
                allowClear={false}
                disabledDate={(current) =>
                  current && current.isAfter(dayjs(), "day")
                }
              />
            </Form.Item>
          </Col>

          <Col>
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              menu={{ items: exportMenuItems }}
              disabled={isExportDisabled}
            >
              <motion.button
                whileHover={{
                  y: -2,
                  boxShadow: "0 8px 16px rgba(14, 124, 134, 0.25)",
                }}
                whileTap={{ scale: 0.96 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  padding: "8px 18px",
                  borderRadius: 10,
                  border: "none",
                  cursor: isExportDisabled ? "not-allowed" : "pointer",
                  opacity: isExportDisabled ? 0.5 : 1,
                  color: "#fff",
                  background: `linear-gradient(135deg, ${THEME.accent} 0%, ${hexToRgba(THEME.accent, 0.85)} 100%)`,
                  boxShadow: `0 4px 10px ${hexToRgba(THEME.accent, 0.3)}`,
                }}
              >
                <DownloadOutlined />
                Export
                <DownOutlined style={{ fontSize: 10 }} />
              </motion.button>
            </Dropdown>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          {TABS.map((tab) => (
            <Col key={tab.key}>
              <MotionTag
                active={activeTab === tab.key}
                color={TAB_COLORS[tab.key] || "#0E7C86"}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </MotionTag>
            </Col>
          ))}
        </Row>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && totalRowCount === 0 && (
        <div style={{ padding: "40px 0" }}>
          <Empty
            description={`No ${activeTabLabel.toLowerCase()} transactions on this date`}
          />
        </div>
      )}

      {!loading &&
        totalRowCount > 0 &&
        sections.map((section) => (
          <DayGlanceSection
            key={section.key}
            label={section.label}
            color={TAB_COLORS[section.key] || "#0E7C86"}
            rows={section.rows}
            totals={section.totals}
          />
        ))}

      {!loading && isAllTab && sections.length > 0 && (
        <div className="dg-card" style={{ borderTop: `4px solid ${THEME.accent}` }}>
          <div className="dg-card-header">
            <h3 className="dg-title" style={{ color: THEME.accent }}>
              Grand Totals
            </h3>
            <span className="dg-title-underline" style={{ background: THEME.accent }} />
          </div>
          <div className="table-container">
            <table className="dg-table">
              <thead>
                <tr>
                  {HEADERS.slice(LABEL_COLUMN_COUNT).map((h) => (
                    <th key={h} style={headerCellStyle(THEME.accent)}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr
                  className="dg-total-row"
                  style={{ background: tintOpaque(THEME.accent, 0.16) }}
                >
                  {NUMERIC_KEYS.map((key) => (
                    <td
                      key={key}
                      className="dg-total-cell"
                      style={{
                        textAlign: "right",
                        color: PAYMENT_COLORS[key] || THEME.accent,
                      }}
                    >
                      {key === "QTY" ? allTotals[key] : fmt2(allTotals[key])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style jsx>{`
        .dg-page-title {
          font-size: 20px;
          font-weight: 700;
        }
        .dg-title-underline {
          display: block;
          width: 56px;
          height: 3px;
          border-radius: 2px;
          margin-top: 4px;
        }

        .dg-card {
          background: #ffffff;
          border: 1px solid #e4e9f0;
          border-radius: 16px;
          box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
          margin-bottom: 24px;
          overflow: hidden;
        }
        .dg-card-header {
          padding: 16px 20px 10px;
        }
        .dg-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }
        .dg-table th {
          border-bottom: 1px solid #e4e9f0;
        }
        .dg-cell {
          padding: 9px 8px;
          font-size: 12px;
          border-bottom: 1px solid #eef1f5;
          background: #ffffff;
          white-space: nowrap;
        }
        .dg-cell-refno {
          font-weight: 700;
          color: #1f2a37;
          text-align: center;
        }
        .dg-cell-date {
          text-align: center;
          color: #6b7280;
          font-size: 11px;
        }
        .dg-cell-cust {
          text-align: left;
          color: #374151;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .dg-sno-cell {
          text-align: center;
          padding: 9px 4px;
          border-bottom: 1px solid #eef1f5;
          background: #ffffff;
        }
        .dg-sno-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          border: 2px solid;
          background: #ffffff;
          font-size: 11px;
          font-weight: 700;
        }
        .dg-total-row td.dg-total-cell {
          padding: 12px 8px;
          font-size: 13px;
          font-weight: 800;
          border-top: 2px solid rgba(0, 0, 0, 0.08);
        }
        .dg-total-row td:first-child {
          border-bottom-left-radius: 8px;
        }
        .dg-total-row td:last-child {
          border-bottom-right-radius: 8px;
        }

        .table-container {
          // overflow-x: auto;
          // overflow-y: auto;
          // -webkit-overflow-scrolling: touch;
          position: relative;
        }
        @media (max-width: 768px) {
          .dg-cell,
          .dg-total-row td.dg-total-cell {
            padding: 8px;
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
};

export default DayGlance;